// bookingSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { appointment } from "src/types";

const initialState: appointment.AppointmentsStateType = {
  latestConsultation: {
    data: null,
    isLoading: false,
    error: null,
  },
  appointments: {
    data: null,
    isLoading: false,
    error: null,
  },
  coach: null,
  nutritionistProfilePicture: "",
  physiotherapistProfilePicture: "",
  psychoanalystProfilePicture: "",
  appointmentTimeSlots: {
    data: null,
    isLoading: false,
    error: null,
  },
  coachList: {
    data: null,
    isLoading: false,
    error: null,
  },
  addAppointment: {
    data: null,
    isLoading: false,
    error: null,
  },
  requestCancellation: {
    data: null,
    isLoading: false,
    error: null,
  },
  finalAmountPaid: 0,
  planPackageDurationRelId: "",
};

const appointmentsSlice = createSlice({
  name: "appointments",
  initialState,
  reducers: {
    getLatestConsultationRequest: (
      state: appointment.AppointmentsStateType,
      action: PayloadAction<any>
    ) => {
      state.latestConsultation.isLoading = true;
      state.latestConsultation.data = null;
      state.latestConsultation.error = null;
    },
    getLatestConsultationSuccess: (
      state: appointment.AppointmentsStateType,
      action: PayloadAction<any>
    ) => {
      state.latestConsultation.isLoading = false;
      state.latestConsultation.data = action.payload;
      state.latestConsultation.error = null;
    },
    getLatestConsultationError: (
      state: appointment.AppointmentsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.latestConsultation.isLoading = false;
      state.latestConsultation.data = null;
      state.latestConsultation.error = error;
    },
    getAppointmentsRequest: (state, action: PayloadAction<any>) => {
      state.appointments.isLoading = true;
      state.appointments.data = null;
      state.appointments.error = null;
    },
    getAppointmentsSuccess: (
      state: appointment.AppointmentsStateType,
      action: PayloadAction<any>
    ) => {
      state.appointments.isLoading = false;
      state.appointments.data = action.payload;
      state.appointments.error = null;
    },
    getAppointmentsError: (
      state: appointment.AppointmentsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.appointments.isLoading = false;
      state.appointments.data = null;
      state.appointments.error = error;
    },
    setCoach: (state, action: PayloadAction<any>) => {
      state.coach = action.payload;
    },
    clearCoach: (state) => {
      state.coach = null;
    },
    setFinalAmountPaid: (state, action: PayloadAction<number>) => {
      state.finalAmountPaid = action.payload;
    },
    clearFinalAmountPaid: (state) => {
      state.finalAmountPaid = 0;
    },
    setPlanPackageDurationRelId: (state, action: PayloadAction<string>) => {
      state.planPackageDurationRelId = action.payload;
    },
    clearPlanPackageDurationRelId: (state) => {
      state.planPackageDurationRelId = "";
    },
    setNutritionistProfilePicture: (state, action: PayloadAction<string>) => {
      state.nutritionistProfilePicture = action.payload;
    },
    setPhysiotherapistProfilePicture: (
      state,
      action: PayloadAction<string>
    ) => {
      state.physiotherapistProfilePicture = action.payload;
    },
    setPsychoanalystProfilePicture: (state, action: PayloadAction<string>) => {
      state.psychoanalystProfilePicture = action.payload;
    },
    getAppointmentTimeSlotsRequest: (state, action: PayloadAction<any>) => {
      state.appointmentTimeSlots.isLoading = true;
      state.appointmentTimeSlots.data = null;
      state.appointmentTimeSlots.error = null;
    },
    getAppointmentTimeSlotsSuccess: (
      state: appointment.AppointmentsStateType,
      action: PayloadAction<any>
    ) => {
      state.appointmentTimeSlots.isLoading = false;
      state.appointmentTimeSlots.data = action.payload;
      state.appointmentTimeSlots.error = null;
    },
    getAppointmentTimeSlotsError: (
      state: appointment.AppointmentsStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.appointmentTimeSlots.isLoading = false;
      state.appointmentTimeSlots.data = null;
      state.appointmentTimeSlots.error = error;
    },
    getCoachListRequest: (state, action: PayloadAction<any>) => {
      state.coachList.isLoading = true;
      state.coachList.data = null;
      state.coachList.error = null;
    },
    getCoachListSuccess: (state, action: PayloadAction<any>) => {
      state.coachList.isLoading = false;
      state.coachList.data = action.payload;
      state.coachList.error = null;
    },
    getCoachListError: (state, action: PayloadAction<any>) => {
      state.coachList.isLoading = false;
      state.coachList.data = null;
      state.coachList.error = action.payload;
    },
    addAppointmentRequest: (state, action: PayloadAction<any>) => {
      state.addAppointment.isLoading = true;
      state.addAppointment.data = null;
      state.addAppointment.error = null;
    },
    addAppointmentSuccess: (state, action: PayloadAction<any>) => {
      state.addAppointment.isLoading = false;
      state.addAppointment.data = action.payload;
      state.addAppointment.error = null;
    },
    addAppointmentError: (state, action: PayloadAction<any>) => {
      state.addAppointment.isLoading = false;
      state.addAppointment.data = null;
      state.addAppointment.error = action.payload;
    },
    requestCancellationRequest: (state, action: PayloadAction<any>) => {
      state.requestCancellation.isLoading = true;
      state.requestCancellation.data = null;
      state.requestCancellation.error = null;
    },
    requestCancellationSuccess: (state, action: PayloadAction<any>) => {
      state.requestCancellation.isLoading = false;
      state.requestCancellation.data = action.payload;
      state.requestCancellation.error = null;
    },
    requestCancellationError: (state, action: PayloadAction<any>) => {
      state.requestCancellation.isLoading = false;
      state.requestCancellation.data = null;
      state.requestCancellation.error = action.payload;
    },
  },
});

export const {
  getAppointmentsRequest,
  getAppointmentsSuccess,
  getAppointmentsError,
  setCoach,
  clearCoach,
  getAppointmentTimeSlotsRequest,
  getAppointmentTimeSlotsSuccess,
  getAppointmentTimeSlotsError,
  getCoachListRequest,
  getCoachListSuccess,
  getCoachListError,
  addAppointmentRequest,
  addAppointmentSuccess,
  addAppointmentError,
  requestCancellationRequest,
  requestCancellationSuccess,
  requestCancellationError,
  setNutritionistProfilePicture,
  setPhysiotherapistProfilePicture,
  setPsychoanalystProfilePicture,
  getLatestConsultationRequest,
  getLatestConsultationSuccess,
  getLatestConsultationError,
  // set final amount paid
  setFinalAmountPaid,
  // clear final amount paid
  clearFinalAmountPaid,
  setPlanPackageDurationRelId,
  clearPlanPackageDurationRelId,
} = appointmentsSlice.actions;
export default appointmentsSlice.reducer;
